import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import DealOverviewCard from './components/DealOverviewCard';
import DealProgressionTracker from './components/DealProgressionTracker';
import OpportunityTable from './components/OpportunityTable';
import RevenueForecastPanel from './components/RevenueForecastPanel';
import CompetitiveAnalysisPanel from './components/CompetitiveAnalysisPanel';

const DealManagementOpportunityCenter = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterProbability, setFilterProbability] = useState('all');

  // Deal overview metrics
  const dealMetrics = {
    activeOpportunities: 28,
    weightedPipeline: 1850000, // PNG Kina
    averageDealSize: 66071, // PNG Kina
    winRate: 42.5 // percentage
  };

  // Deal progression stages
  const dealStages = [
    { stage: 'Initial Contact', icon: 'Phone', color: 'bg-blue-500' },
    { stage: 'Qualification', icon: 'CheckCircle', color: 'bg-indigo-500' },
    { stage: 'Proposal', icon: 'FileText', color: 'bg-purple-500' },
    { stage: 'Negotiation', icon: 'MessageSquare', color: 'bg-pink-500' },
    { stage: 'Contract Signing', icon: 'PenTool', color: 'bg-emerald-500' }
  ];

  // Opportunities data
  const opportunitiesData = [
    {
      id: 1,
      company: 'PNG Department of Finance',
      dealValue: 250000,
      probability: 85,
      stage: 'Negotiation',
      expectedClose: '2026-02-15',
      daysInStage: 12,
      assignedTo: 'Sarah M.',
      competitor: 'Local HR Software',
      competitivePosition: 'Strong',
      lastActivity: '2026-01-19',
      nextMilestone: 'Final pricing review'
    },
    {
      id: 2,
      company: 'Provincial Health Authority',
      dealValue: 180000,
      probability: 65,
      stage: 'Proposal',
      expectedClose: '2026-03-01',
      daysInStage: 8,
      assignedTo: 'David K.',
      competitor: 'Manual Process',
      competitivePosition: 'Moderate',
      lastActivity: '2026-01-18',
      nextMilestone: 'Proposal presentation'
    },
    {
      id: 3,
      company: 'National Education Board',
      dealValue: 320000,
      probability: 75,
      stage: 'Contract Signing',
      expectedClose: '2026-02-05',
      daysInStage: 5,
      assignedTo: 'Sarah M.',
      competitor: 'None',
      competitivePosition: 'Exclusive',
      lastActivity: '2026-01-20',
      nextMilestone: 'Contract execution'
    },
    {
      id: 4,
      company: 'Port Moresby City Council',
      dealValue: 145000,
      probability: 45,
      stage: 'Qualification',
      expectedClose: '2026-03-20',
      daysInStage: 15,
      assignedTo: 'Michael T.',
      competitor: 'International Vendor',
      competitivePosition: 'Weak',
      lastActivity: '2026-01-17',
      nextMilestone: 'Budget confirmation'
    },
    {
      id: 5,
      company: 'PNG Customs Service',
      dealValue: 95000,
      probability: 55,
      stage: 'Proposal',
      expectedClose: '2026-02-28',
      daysInStage: 10,
      assignedTo: 'David K.',
      competitor: 'Spreadsheet Solution',
      competitivePosition: 'Strong',
      lastActivity: '2026-01-19',
      nextMilestone: 'Technical demo'
    }
  ];

  // Revenue forecast data
  const forecastData = {
    q1: { expected: 750000, confidence: 'high', deals: 8 },
    q2: { expected: 920000, confidence: 'medium', deals: 12 },
    q3: { expected: 680000, confidence: 'medium', deals: 9 },
    q4: { expected: 1100000, confidence: 'low', deals: 15 }
  };

  // Competitive intelligence
  const competitiveData = {
    competitors: [
      { name: 'Local HR Software', mentions: 8, winRate: 35 },
      { name: 'International Vendor', mentions: 5, winRate: 25 },
      { name: 'Manual Process', mentions: 12, winRate: 60 },
      { name: 'Spreadsheet Solution', mentions: 7, winRate: 55 }
    ],
    winFactors: [
      'PNG-specific compliance features',
      'Local support and training',
      'Competitive pricing',
      'Government sector experience'
    ],
    lossFactors: [
      'Budget constraints',
      'Change management resistance',
      'Existing vendor relationships'
    ]
  };

  // Filter opportunities
  const filteredOpportunities = opportunitiesData?.filter(opp => {
    const matchesSearch = opp?.company?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStage = filterStage === 'all' || opp?.stage === filterStage;
    const matchesProbability = filterProbability === 'all' || 
      (filterProbability === 'high' && opp?.probability >= 70) ||
      (filterProbability === 'medium' && opp?.probability >= 40 && opp?.probability < 70) ||
      (filterProbability === 'low' && opp?.probability < 40);
    return matchesSearch && matchesStage && matchesProbability;
  });

  const formatCurrency = (amount) => {
    return `K${amount?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 70) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    if (probability >= 40) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Deal Management & Opportunity Center
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Comprehensive sales opportunity tracking with revenue forecasting and competitive analysis
          </p>
        </div>

        {/* Deal Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <DealOverviewCard
            title="Active Opportunities"
            value={dealMetrics?.activeOpportunities}
            icon="Briefcase"
            color="blue"
          />
          <DealOverviewCard
            title="Weighted Pipeline"
            value={formatCurrency(dealMetrics?.weightedPipeline)}
            icon="TrendingUp"
            color="emerald"
          />
          <DealOverviewCard
            title="Average Deal Size"
            value={formatCurrency(dealMetrics?.averageDealSize)}
            icon="DollarSign"
            color="purple"
          />
          <DealOverviewCard
            title="Win Rate"
            value={`${dealMetrics?.winRate}%`}
            icon="Award"
            color="amber"
          />
        </div>

        {/* Deal Progression Tracker */}
        <div className="mb-8">
          <DealProgressionTracker stages={dealStages} />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Revenue Forecast */}
          <div className="lg:col-span-2">
            <RevenueForecastPanel forecast={forecastData} formatCurrency={formatCurrency} />
          </div>

          {/* Competitive Analysis */}
          <div className="lg:col-span-1">
            <CompetitiveAnalysisPanel data={competitiveData} />
          </div>
        </div>

        {/* Opportunity Table */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
          <div className="p-6 border-b border-gray-200 dark:border-slate-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Opportunity Pipeline
              </h2>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-3">
                <div className="relative">
                  <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search opportunities..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-slate-700 dark:text-white"
                  />
                </div>
                
                <select
                  value={filterStage}
                  onChange={(e) => setFilterStage(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-slate-700 dark:text-white"
                >
                  <option value="all">All Stages</option>
                  <option value="Initial Contact">Initial Contact</option>
                  <option value="Qualification">Qualification</option>
                  <option value="Proposal">Proposal</option>
                  <option value="Negotiation">Negotiation</option>
                  <option value="Contract Signing">Contract Signing</option>
                </select>
                
                <select
                  value={filterProbability}
                  onChange={(e) => setFilterProbability(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-slate-700 dark:text-white"
                >
                  <option value="all">All Probabilities</option>
                  <option value="high">High (≥70%)</option>
                  <option value="medium">Medium (40-69%)</option>
                  <option value="low">Low (&lt;40%)</option>
                </select>
              </div>
            </div>
          </div>

          <OpportunityTable 
            opportunities={filteredOpportunities}
            formatCurrency={formatCurrency}
            getProbabilityColor={getProbabilityColor}
          />
        </div>

        {/* Export Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2">
            <Icon name="Download" size={16} />
            Export Pipeline Report
          </button>
          <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add New Opportunity
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealManagementOpportunityCenter;